import { DangerousCharacterRegex } from '../constants/regex';

/**
 * It removes all keys from an object that have a value of `undefined`, `null`, `''`, or an empty array
 * @param {any} object - The object to sanitize.
 * @returns An object with all the keys that have a value of undefined, null, or an empty string
 * removed.
 */
export const sanitizeObject = (object: any) => {
    if (typeof object !== 'object' || Array.isArray(object)) {
        return object;
    }

    let keysToUnset: string[] = [];
    Object.keys(object ?? {}).forEach((key) => {
        let value = object[key];
        if (value === undefined || value === null) keysToUnset.push(key);
        if (typeof value === 'string' && value === '') keysToUnset.push(key);
        if (typeof value === 'object' && !Array.isArray(value)) {
            let newObj = sanitizeObject(value);
            if (!Object.keys(newObj ?? {}).length) {
                keysToUnset.push(key);
            } else {
                object[key] = newObj;
            }
        }

        if (Array.isArray(value)) {
            if (!value.length) {
                keysToUnset.push(key);
            } else if (typeof value[0] === 'object') {
                for (let i = 0; i < value.length; i++) {
                    value[i] = sanitizeObject(value[i]);
                }
                value = value.filter((obj) => Object.keys(obj).length);
                if (!value.length) {
                    keysToUnset.push(key);
                }
            }
        }
    });

    keysToUnset.forEach((key) => {
        delete object[key];
    });

    return object;
};

export const convertArrayToObject = (array: any[], key: string) => {
    return array.reduce((output, elem) => {
        const letter = elem[key];
        output[letter] = elem;
        return output;
    }, {});
};

export const titlecase = (input: string): string | null => {
    if (!input) {
        return null;
    }
    return input
        .split(' ')
        .map((w) => w[0]?.toUpperCase() + w.substr(1).toLowerCase())
        .join(' ');
};

export const unslugify = (
    input: any,
    titleCase: boolean = false,
): any | null => {
    if (!input) {
        return null;
    }
    return titleCase
        ? titlecase(input.replace(/_/g, ' '))
        : input.replace(/_/g, ' ');
};

export const slugify = (input: string): string =>
    input
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, ' ')
        .replace(/[\s-]+/g, '_');

export const groupBy = <T, K extends keyof T>(
    array: T[],
    property: K,
): Record<string, T[]> => {
    return array.reduce((acc, obj) => {
        const key = String(obj[property]);
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
    }, {} as Record<string, T[]>);
};

export const objectsAreEqual = (objA: any, objB: any) => {
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    if (keysA.length !== keysB.length) {
        return false;
    }

    for (const key of keysA) {
        if (objA[key] !== objB[key]) {
            return false;
        }
    }

    return true;
};

export const toSnakeCase = (input: string): string => {
    if (input.trim() === '') {
        throw new Error('Invalid input: String cannot be empty');
    }

    return input
        .trim()
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .replace(/[\W_]+/g, '_')
        .toLowerCase();
};

export const validJSONFormat = (input: string): string => {
    const spaceRegex = /\s/;
    const doubleQuoteRegex = /"/;

    if (doubleQuoteRegex.test(input) || spaceRegex.test(input)) {
        throw new Error('Invalid input: Space and double quote not allowed');
    }

    return input.trim();
};

// Matches values like +1, -3, +0.5, -.5 — simple signed numbers that are safe in Excel
const SignedNumericRegex = /^[+-](\d+\.?\d*|\.\d+)$/;

export const sanitizeForExcel = (value: string | number | null | undefined) => {
    // Keep null/undefined empty
    if (value === null || value === undefined || value.toString().trim() === '')
        return '';

    const stringValue = value.toString().trim();

    // If the value starts with a dangerous character, prefix it with a single quote (')
    if (DangerousCharacterRegex.test(stringValue)) {
        // Allow simple signed numbers like +1, -3, +0.5 — these are not formula injection vectors
        if (SignedNumericRegex.test(stringValue)) {
            return stringValue;
        }
        return `'${stringValue}`;
    }

    return stringValue;
};

export const stringToArray = (val: string): string[] => {
    if (!val) return [];
    return val
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
};
